import { useProperty, useSnowplowTracking, useTranslation } from '@eti/providers';
import { Stack, Text, textVariants } from '@etg/wings';
import { Link } from 'react-router-dom';
import { routes } from '../../../../../constants/routesAndApisConstants';
import CardWithActionButton from '../../common/CardWithActionButton';
import ContactUsDrawerButton from '../common/ContactUsDrawerButton';
import type { RebookEligibilityType } from './models';
import { RebookEligibility } from './models';

interface ChangeableOrderByCustomerProps {
  rebookEligibilityType: RebookEligibilityType;
  shouldShowContactMethods?: boolean;
  shouldShowChangeTripButton?: boolean;
}

const ChangeableOrderByCustomer = ({
  rebookEligibilityType,
  shouldShowContactMethods = true,
  shouldShowChangeTripButton = true,
}: ChangeableOrderByCustomerProps) => {
  const { t } = useTranslation();
  const { p } = useProperty();
  const { isSnowplowTrackingEnabled, sendTrackEvent } = useSnowplowTracking();

  const isChangeTripButtonEnabled = p('OrderDetails.ChangeTrip.ChangeTripLink.Enabled');

  const handleOnCardClick = () => {
    if (isSnowplowTrackingEnabled) {
      sendTrackEvent({
        category: 'Manage Trip',
        action: 'Click',
        label: 'RECALCULATE_PRICE',
      });
    }
  };

  const getTextsPerEligibilityType = (type: RebookEligibilityType) => {
    switch (type) {
      case RebookEligibility.CHANGE_FLEXIBLE_TICKET_YOURSELF:
        return {
          heading: t('OrderDetails.ManageTrip.FlexibleTicket.Title'),
          recalculationSectionDescription: (
            <Text>
              {t('OrderDetails.ManageTrip.CheckPriceDateChange.FlexibleTicket.Description')}
            </Text>
          ),
          priceReviewDescription: t(
            'OrderDetails.ManageTrip.HowDoIChange.FlexibleTicket.PriceReview.Description',
          ),
        };
      default:
        return {
          heading: t('OrderDetails.ManageTrip.ChangeableTicket.Title'),
          priceReviewDescription: t('OrderDetails.ManageTrip.HowDoIChange.PriceReview.Description'),
        };
    }
  };

  const { heading, recalculationSectionDescription, priceReviewDescription } =
    getTextsPerEligibilityType(rebookEligibilityType);

  return (
    <Stack data-testid="changeable-order-by-customer">
      <Text variant={textVariants._16_BOLD}>{heading}</Text>
      {isChangeTripButtonEnabled && shouldShowChangeTripButton && (
        <CardWithActionButton
          as={Link}
          buttonText={t('OrderDetails.ManageTrip.ChangeTrip.CheckPriceDateChange.Button.Text')}
          onClick={handleOnCardClick}
          to={routes.CHANGE_TRIP}
        >
          <Stack spacing={8}>
            <Text variant={textVariants._16_BOLD}>
              {t('OrderDetails.ManageTrip.ChangeTrip.CheckPriceDateChange.Button')}
            </Text>
            {recalculationSectionDescription}
          </Stack>
        </CardWithActionButton>
      )}
      <Stack spacing={24}>
        <Stack spacing={8}>
          <Text variant={textVariants._14_BOLD}>
            {t('OrderDetails.ManageTrip.HowDoIChange.Header')}
          </Text>
          <Text asHtml>{priceReviewDescription}</Text>
        </Stack>
        {shouldShowContactMethods && (
          <ContactUsDrawerButton
            chatType="oriana"
            snowplow={{
              property: 'RESCHEDULE',
              label: 'CONTACT_US',
              category: 'Manage Trip',
            }}
          />
        )}
      </Stack>
    </Stack>
  );
};

export default ChangeableOrderByCustomer;
